/**
 * OptionStrategyDetailPage — Phase 3
 * Review screen for a saved option strategy.
 * All data is mock/local — no backend calls.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, ChevronRight, Rocket, Edit2, Info, BarChart2,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ROUTES } from '../../routes/constants';
import { optionsApi } from '../../api/client';

// ─── Local helpers ────────────────────────────────────────────────────────────

function strikeLabel(mode, offset = 1) {
  if (mode === 'ATM')     return 'ATM';
  if (mode === 'ITM')     return offset >= 2 ? 'ITM2' : 'ITM1';
  if (mode === 'OTM')     return offset >= 2 ? 'OTM2' : 'OTM1';
  if (mode === 'premium') return 'Premium';
  return mode ?? '—';
}

function expiryShort(val) {
  return (
    { current_week: 'Cur Wk', next_week: 'Next Wk',
      current_month: 'Cur Mo', next_month: 'Next Mo' }[val] ?? val ?? '—'
  );
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_STRATEGIES = {
  'bull-call-spread': {
    name: 'Bull Call Spread', family: 'bullish',
    underlying: 'NIFTY', expiry: 'current_week', lotSize: 75,
    createdAt: '2025-03-10', status: 'draft',
    legs: [
      { action: 'BUY',  optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 1, ratio: 1 },
      { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1, lots: 1, ratio: 1 },
    ],
  },
  'bear-put-spread': {
    name: 'Bear Put Spread', family: 'bearish',
    underlying: 'BANKNIFTY', expiry: 'current_week', lotSize: 15,
    createdAt: '2025-03-11', status: 'draft',
    legs: [
      { action: 'BUY',  optionType: 'PE', strikeMode: 'ATM', offset: 0, lots: 1, ratio: 1 },
      { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1, lots: 1, ratio: 1 },
    ],
  },
  'long-straddle': {
    name: 'Long Straddle', family: 'volatility',
    underlying: 'BANKNIFTY', expiry: 'current_week', lotSize: 15,
    createdAt: '2025-03-11', status: 'saved',
    legs: [
      { action: 'BUY', optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 2, ratio: 1 },
      { action: 'BUY', optionType: 'PE', strikeMode: 'ATM', offset: 0, lots: 2, ratio: 1 },
    ],
  },
  'short-straddle': {
    name: 'Short Straddle', family: 'volatility',
    underlying: 'NIFTY', expiry: 'current_week', lotSize: 75,
    createdAt: '2025-03-12', status: 'draft',
    legs: [
      { action: 'SELL', optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 1, ratio: 1 },
      { action: 'SELL', optionType: 'PE', strikeMode: 'ATM', offset: 0, lots: 1, ratio: 1 },
    ],
  },
  'iron-condor': {
    name: 'Iron Condor', family: 'neutral',
    underlying: 'NIFTY', expiry: 'current_month', lotSize: 75,
    createdAt: '2025-03-12', status: 'saved',
    legs: [
      { action: 'BUY',  optionType: 'PE', strikeMode: 'OTM', offset: 2, lots: 1, ratio: 1 },
      { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1, lots: 1, ratio: 1 },
      { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1, lots: 1, ratio: 1 },
      { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 2, lots: 1, ratio: 1 },
    ],
  },
  'iron-fly': {
    name: 'Iron Fly', family: 'neutral',
    underlying: 'FINNIFTY', expiry: 'current_week', lotSize: 40,
    createdAt: '2025-03-13', status: 'draft',
    legs: [
      { action: 'BUY',  optionType: 'PE', strikeMode: 'OTM', offset: 1, lots: 1, ratio: 1 },
      { action: 'SELL', optionType: 'PE', strikeMode: 'ATM', offset: 0, lots: 1, ratio: 1 },
      { action: 'SELL', optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 1, ratio: 1 },
      { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 1, lots: 1, ratio: 1 },
    ],
  },
};

function getMockStrategy(id) {
  if (MOCK_STRATEGIES[id]) return { id, ...MOCK_STRATEGIES[id] };
  // Safe fallback for unknown ids
  return {
    id,
    name: id
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    family: 'custom',
    underlying: 'NIFTY',
    expiry: 'current_week',
    lotSize: 75,
    createdAt: '—',
    status: 'draft',
    legs: [
      { action: 'BUY', optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 1, ratio: 1 },
    ],
  };
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const FAMILY_BADGE = {
  bullish:    'bg-bull/10 text-bull border-bull/20',
  bearish:    'bg-bear/10 text-bear border-bear/20',
  neutral:    'bg-info/10 text-info border-info/20',
  volatility: 'bg-amber/10 text-amber border-amber/20',
  custom:     'bg-bg-elevated text-txt-muted border-bg-border',
};

const STATUS_BADGE = {
  draft:    'bg-bg-elevated text-txt-muted border-bg-border',
  saved:    'bg-amber/10 text-amber border-amber/20',
  deployed: 'bg-bull/10 text-bull border-bull/20',
};

// ─── Summary computation (heuristic) ─────────────────────────────────────────

function computeSummary(legs) {
  const totalBuyLots  = legs.filter(l => l.action === 'BUY' ).reduce((s, l) => s + l.lots, 0);
  const totalSellLots = legs.filter(l => l.action === 'SELL').reduce((s, l) => s + l.lots, 0);
  const netPremium =
    totalBuyLots > totalSellLots  ? 'Net Debit' :
    totalSellLots > totalBuyLots  ? 'Net Credit' : 'Neutral';
  const maxProfit =
    totalSellLots > totalBuyLots  ? 'Capped' : 'Unlimited';
  const maxLoss =
    totalBuyLots > totalSellLots  ? 'Capped' : 'Unlimited';
  return { maxProfit, maxLoss, netPremium, breakeven: '—' };
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, valueClass }) {
  return (
    <div className="bg-bg-surface/60 border border-bg-border/70 rounded-lg px-3 py-2.5 flex flex-col gap-0.5">
      <div className="field-label">{label}</div>
      <div className={cn('text-sm font-semibold font-mono', valueClass || 'text-txt-primary')}>
        {value}
      </div>
    </div>
  );
}

// ─── Meta row ─────────────────────────────────────────────────────────────────

function MetaRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="field-label">{label}</span>
      <span className="text-xs font-mono text-txt-primary">{value}</span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function OptionStrategyDetailPage() {
  const navigate        = useNavigate();
  const { id }          = useParams();
  const location        = useLocation();
  const stateStrategy   = location.state?.strategy ?? null;
  const [strategy, setStrategy] = useState(() => stateStrategy ?? getMockStrategy(id));
  const [strategySource, setStrategySource] = useState(() => (stateStrategy ? 'local' : 'mock'));
  const [isLoading, setIsLoading] = useState(() => !stateStrategy);
  const [deployments, setDeployments] = useState([]);
  const [deploymentsLoaded, setDeploymentsLoaded] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    async function loadStrategy() {
      setIsLoading(true);
      setDeployments([]);
      setDeploymentsLoaded(false);
      try {
        const remote = await optionsApi.get(id);
        if (isCancelled) return;
        setStrategy(remote);
        setStrategySource('backend');
        try {
          const rows = await optionsApi.listDeployments(remote.id);
          if (isCancelled) return;
          setDeployments(Array.isArray(rows) ? rows : []);
        } catch {
          if (isCancelled) return;
          setDeployments([]);
        } finally {
          if (!isCancelled) setDeploymentsLoaded(true);
        }
      } catch {
        if (isCancelled) return;
        if (stateStrategy) {
          setStrategy(stateStrategy);
          setStrategySource('local');
        } else {
          setStrategy(getMockStrategy(id));
          setStrategySource('mock');
        }
        setDeploymentsLoaded(true);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    }

    loadStrategy();
    return () => { isCancelled = true; };
  }, [id, stateStrategy]);

  const legs     = strategy.legs ?? [];
  const summary  = useMemo(() => computeSummary(legs), [legs]);
  const createdAtLabel = String(strategy.createdAt || '-').slice(0, 10);

  const netPremiumClass =
    summary.netPremium === 'Net Debit'  ? 'text-bear' :
    summary.netPremium === 'Net Credit' ? 'text-bull' : 'text-txt-muted';

  function handleEdit() {
    navigate(ROUTES.OPTIONS_NEW, { state: { mode: 'edit', strategy } });
  }

  function handleDeploy() {
    navigate(ROUTES.optionsDeploy(id), { state: { strategy } });
  }

  return (
    <div className="flex flex-col gap-4 max-w-[1100px]">

      {/* ── 1. Breadcrumb + actions ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => navigate(ROUTES.OPTIONS)}
            className="btn-ghost p-1.5 shrink-0"
            title="Back to options"
          >
            <ArrowLeft size={14} />
          </button>
          <button
            type="button"
            onClick={() => navigate(ROUTES.OPTIONS)}
            className="text-2xs font-mono text-txt-muted hover:text-txt-secondary transition-colors shrink-0"
          >
            O For Option
          </button>
          <ChevronRight size={10} className="text-txt-muted shrink-0" />
          <span className="section-title truncate">{strategy.name}</span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={handleEdit} className="btn-secondary text-xs">
            <Edit2 size={12} />
            Edit
          </button>
          <button type="button" onClick={handleDeploy} className="btn-primary text-xs">
            <Rocket size={12} />
            Deploy
          </button>
        </div>
      </div>

      {/* ── 2. Strategy info panel ── */}
      <div className="panel p-5">
        <div className="flex flex-col lg:flex-row gap-5">

          {/* Left — meta */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            <div>
              <h2 className="font-display font-bold text-lg text-txt-primary leading-snug">
                {strategy.name}
              </h2>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {strategy.family && (
                  <span className={cn('badge border text-2xs font-mono', FAMILY_BADGE[strategy.family] ?? FAMILY_BADGE.custom)}>
                    {strategy.family}
                  </span>
                )}
                {(() => {
                  const isDeployed = deployments.length > 0;
                  const baseStatus = strategy.status === 'deployed' ? 'saved' : strategy.status;
                  const displayStatus = isDeployed ? 'deployed' : baseStatus;
                  return (
                    <span className={cn('badge border text-2xs font-mono', STATUS_BADGE[displayStatus] ?? STATUS_BADGE.draft)}>
                      {displayStatus}
                    </span>
                  );
                })()}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <MetaRow label="Underlying"  value={strategy.underlying} />
              <MetaRow label="Lot Size"    value={String(strategy.lotSize)} />
              <MetaRow label="Expiry"      value={expiryShort(strategy.expiry)} />
              <MetaRow label="Created"     value={createdAtLabel} />
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px bg-bg-border/60 self-stretch" />
          <div className="block lg:hidden h-px bg-bg-border/60" />

          {/* Right — summary cards */}
          <div className="lg:w-[340px] shrink-0 grid grid-cols-2 gap-2 content-start">
            <SummaryCard
              label="Max Profit"
              value={summary.maxProfit}
              valueClass={summary.maxProfit === 'Unlimited' ? 'text-bull' : 'text-txt-primary'}
            />
            <SummaryCard
              label="Max Loss"
              value={summary.maxLoss}
              valueClass={summary.maxLoss === 'Unlimited' ? 'text-bear' : 'text-txt-primary'}
            />
            <SummaryCard label="Breakeven" value={summary.breakeven} />
            <SummaryCard
              label="Net Premium"
              value={summary.netPremium}
              valueClass={netPremiumClass}
            />
          </div>
        </div>
      </div>

      {/* ── 3. Legs table ── */}
      <div className="panel flex flex-col">
        <div className="px-4 py-2.5 border-b border-bg-border/70 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="section-title">Legs</span>
            <span className="badge bg-bg-elevated text-txt-muted border border-bg-border text-2xs font-mono">
              {legs.length}
            </span>
          </div>
          <span className="text-2xs font-mono text-txt-muted hidden sm:inline">
            Qty = lots × lot size ({strategy.lotSize})
          </span>
        </div>

        {/* Table header */}
        <div
          className="grid px-4 py-1.5 border-b border-bg-border/50 bg-bg-surface/20"
          style={{ gridTemplateColumns: '28px 72px 56px 88px 80px 48px 72px' }}
        >
          {['#', 'Action', 'Type', 'Expiry', 'Strike', 'Lots', 'Qty'].map((h, i) => (
            <span key={i} className="field-label text-center">{h}</span>
          ))}
        </div>

        {/* Leg rows */}
        <div className="overflow-x-auto">
          {legs.length === 0 ? (
            <div className="py-10 flex flex-col items-center gap-2 text-txt-muted">
              <BarChart2 size={20} className="opacity-20" />
              <span className="text-2xs font-mono">No legs defined.</span>
            </div>
          ) : (
            legs.map((leg, idx) => {
              const isBuy = leg.action === 'BUY';
              const isCE  = leg.optionType === 'CE';
              const qty   = leg.lots * strategy.lotSize;
              return (
                <div
                  key={idx}
                  className="grid px-4 py-2.5 border-b border-bg-border/40 items-center"
                  style={{ gridTemplateColumns: '28px 72px 56px 88px 80px 48px 72px' }}
                >
                  <span className="text-2xs font-mono text-txt-muted text-center">{idx + 1}</span>

                  <span className={cn(
                    'text-2xs font-mono font-semibold px-2 py-1 rounded border text-center',
                    isBuy
                      ? 'bg-bull/10 text-bull border-bull/25'
                      : 'bg-bear/10 text-bear border-bear/25'
                  )}>
                    {leg.action}
                  </span>

                  <span className={cn(
                    'text-2xs font-mono font-semibold px-2 py-1 rounded border text-center',
                    isCE
                      ? 'bg-info/10 text-info border-info/25'
                      : 'bg-warn/10 text-warn border-warn/25'
                  )}>
                    {leg.optionType}
                  </span>

                  <span className="text-2xs font-mono text-txt-secondary text-center">
                    {expiryShort(leg.expiry || strategy.expiry)}
                  </span>

                  <span className="text-2xs font-mono text-txt-primary text-center font-medium">
                    {strikeLabel(leg.strikeMode, leg.offset)}
                  </span>

                  <span className="text-2xs font-mono text-txt-secondary text-center tabular-nums">
                    {leg.lots}
                  </span>

                  <span className="text-2xs font-mono text-amber font-semibold text-center tabular-nums">
                    {qty}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── 4. Deployments ── */}
      {strategySource === 'backend' && (
        <div className="panel flex flex-col">
          <div className="px-4 py-2.5 border-b border-bg-border/70 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <span className="section-title">Deployments</span>
              <span className="badge bg-bg-elevated text-txt-muted border border-bg-border text-2xs font-mono">
                {deployments.length}
              </span>
            </div>
          </div>

          {!deploymentsLoaded ? (
            <div className="px-4 py-4 text-2xs font-mono text-txt-muted">Loading…</div>
          ) : deployments.length === 0 ? (
            <div className="px-4 py-4 text-2xs font-mono text-txt-muted">Not deployed</div>
          ) : (
            <>
              <div
                className="grid px-4 py-1.5 border-b border-bg-border/50 bg-bg-surface/20"
                style={{ gridTemplateColumns: '72px 96px 88px 1fr' }}
              >
                {['Mode', 'Trading', 'Status', 'Created'].map((h, i) => (
                  <span key={i} className="field-label">{h}</span>
                ))}
              </div>
              {deployments.map((d) => {
                const isLive = String(d.deployMode || '').toLowerCase() === 'live';
                const created = String(d.createdAt || '—').slice(0, 10);
                return (
                  <div
                    key={d.id}
                    className="grid px-4 py-2.5 border-b border-bg-border/40 items-center"
                    style={{ gridTemplateColumns: '72px 96px 88px 1fr' }}
                  >
                    <span className={cn(
                      'text-2xs font-mono font-semibold px-2 py-1 rounded border w-fit',
                      isLive
                        ? 'bg-bear/10 text-bear border-bear/25'
                        : 'bg-info/10 text-info border-info/25'
                    )}>
                      {isLive ? 'live' : 'paper'}
                    </span>
                    <span className="text-2xs font-mono text-txt-secondary">
                      {d.tradingMode}
                    </span>
                    <span className="text-2xs font-mono text-txt-primary">
                      {d.status}
                    </span>
                    <span className="text-2xs font-mono text-txt-muted">
                      {created}
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ── 5. Data source notice ── */}
      <div className="flex items-center gap-1.5 px-1">
        <Info size={11} className="text-txt-muted shrink-0" />
        <span className="text-2xs font-mono text-txt-muted">
          {isLoading && 'Loading strategy… '}
          {!isLoading && strategySource === 'backend' && `Loaded from backend · ID: ${id}`}
          {!isLoading && strategySource === 'local' && `Local preview fallback · ID: ${id}`}
          {!isLoading && strategySource === 'mock' && `Mock data fallback · ID: ${id}`}
        </span>
      </div>

    </div>
  );
}
